import {IBaseSearchParam} from '../Types/ISearchParam';
import BackendUrl from '../Constants/BackendUrl';
import Environment from '../Enum/Environment';
import {IGenericObject} from '../Types/IGenericObject';
import {ISearchResponse} from '../Types/ISearchResponse';
import {IResultsData} from '../Types/IResultsData';
import SearchState from '../Enum/SearchState';
import {ISearchQueue} from '../Types/ISearchContext';
import {ISearchFilterSet, ISelectedFilters} from '../Types/ISearchFilter';
import {getActiveFiltersSelectedItems} from '../Utils/SearchFilterUtils';
import {setRequestConfig} from './RequestConfig';
import IHardSearchLimits from '../Types/IHardSearchLimits';

export const createSearchReference = (env: Environment, payload: IBaseSearchParam): Promise<ISearchResponse> => {
    return new Promise<ISearchResponse>((resolve: (value: ISearchResponse) => void, reject) => {
        fetch(`${BackendUrl[env]}/api/searches`, setRequestConfig('POST', { payload: JSON.stringify(payload) }))
            .then((response: Response) => response.json())
            .then(data => {
                const payload: ISearchResponse = {...data};
                payload.parameters = JSON.parse(data.parameters);
                resolve(payload);
            })
            .catch((err: Error) => reject(err));
    });
}

export const checkSearchReferenceStatus = (env: Environment, refId: string): Promise<ISearchResponse> => {
    return new Promise<ISearchResponse>((resolve, reject) => {
        fetch(`${BackendUrl[env]}/api/searches/${refId}`, setRequestConfig('GET'))
            .then((response: Response) => response.json())
            .then(data => {
                const payload: ISearchResponse = {...data};
                try {
                    payload.parameters = JSON.parse(data.parameters);
                    resolve(payload);
                } catch {
                    reject(null);
                }
            })
            .catch((err: Error) => reject(err));
    })
}

export const getResultFileUrl = (env: Environment, refId: string): Promise<IGenericObject> => {
    return new Promise<IGenericObject>((resolve, reject) => {
        fetch(`${BackendUrl[env]}/api/searches/${refId}/download`, setRequestConfig('GET'))
            .then((response: Response) => response.json())
            .then((data) => {
                resolve(data);
            })
            .catch((err: Error) => reject(err));
    });
}

export const loadResultFile = (url: string): Promise<IResultsData[]> => {
    return new Promise<IResultsData[]>((resolve, reject) => {
        fetch(url, setRequestConfig('GET', { noAuth: 'true' }))
            .then((response: Response) => response.json())
            .then((data: IResultsData[]) => resolve(data))
            .catch((err: Error) => reject(err))
    });
}

export const fetchResult = (env: Environment, refId: string): Promise<IResultsData[]> => {
    return new Promise<IResultsData[]>((resolve, reject) => {
        getResultFileUrl(env, refId)
            .then((response) => {
                loadResultFile(response.url)
                    .then((data) => {
                       resolve(data);
                    })
                    .catch(() => reject(`failed to load result from ${response.url}`));
            })
            .catch(() => reject('failed to get file url'));
    });
}

export const loopRefStatus = (
    env: Environment,
    queue: ISearchQueue,
    callback: (finishedQueue: ISearchQueue) => void): void => {
    const currentQueue: ISearchQueue = {...queue};
    const refRequest = (waitTime: number): number => {
        return window.setTimeout(() => {
            checkSearchReferenceStatus(env, currentQueue.ref)
                .then((response: ISearchResponse) => {
                    currentQueue.searchState = response.state;
                    if (response.state === SearchState.STATE_CAR_EXPORTED) {
                        currentQueue.isFinished = true;
                        callback(currentQueue);
                        clearTimeout(reqInterval);
                    } else {
                        refRequest(5000);
                    }
                })
                .catch((err) => console.error(err));
        }, waitTime)
    }
    const reqInterval: number = refRequest(0);
}

export const storeReportFilter = (ref: string, activeFilters: ISearchFilterSet | null): Promise<IGenericObject | null> => {
    return new Promise<IGenericObject | null>((resolve, reject) => {
        const selectedFilters: ISelectedFilters | null = getActiveFiltersSelectedItems(activeFilters);
        if (selectedFilters === null) {
            reject();
            return;
        }

        const filter: IGenericObject = {};
        Object.keys(selectedFilters).forEach((key) => {
            if (selectedFilters[key] !== null) {
                filter[key] = selectedFilters[key];
            }
        });

        // Set and forget
        fetch(`/api/search/filter`, setRequestConfig('PATCH', {
            payload: JSON.stringify({
                reference: ref,
                filter
            })
        }))
            .then(() => {
                resolve(filter)
            })
            .catch(() => {
                reject();
            });
    });
}

export const getHardLimits = (token: string): Promise<IHardSearchLimits> => {
    return new Promise<IHardSearchLimits>((resolve, reject) => {
        fetch('/api/account', {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
                .then((response: Response) => response.json())
                .then((data: IHardSearchLimits) => resolve(data))
                .catch((err: Error) => reject(err))
    });
}

export const saveReportToAWS = (token: string, blob: Blob, filename: string): Promise<any> => {
    return new Promise<any>((resolve, reject) => {
        const formData = new FormData();
        formData.append("file", blob, filename);
        fetch('/api/search/report', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`
                },
                body: formData
            })
                .then((data: any) => resolve(data))
                .catch((err: Error) => reject(err))
    });
}
